import * as React from 'react';
import {Button} from 'components';
import Toast from 'components/Basic/Toast';
import {selectUserProfile} from 'features/User';
import {useQuery} from 'hooks';
import {useTranslation} from 'react-i18next';
import {useDispatch, useSelector} from 'react-redux';
import {useHistory} from 'react-router';
import {BookingService} from 'services';

import {
  bookAppointment,
  setDirectBookingSelectedSession,
} from '../directBookingActions';
import {
  getDirectBookingSelectedProvider,
  selectProvider,
} from '../directBookingSelectors';
import {useDirectBookingFilter} from '../useDirectBookingFilter';

import {useDirectBookingAvailability} from './useDirectBookingAvailability';

type Props = {
  filter: ReturnType<typeof useDirectBookingFilter>;
  availability: ReturnType<typeof useDirectBookingAvailability>;
  providerId: string;
};

export function DirectBookingSubmitButton({
  filter,
  availability: {
    appointmentType,
    appointmentTypeID,
    duration,
    isLoading,
    selectedTime,
    providerRole,
    state,
    watch,
    slots,
  },
  providerId,
}: Readonly<Props>) {
  const history = useHistory();
  const dispatch = useDispatch();
  const user = useSelector(selectUserProfile);
  const selectedProvider = useSelector(getDirectBookingSelectedProvider);
  const providerById = useSelector(selectProvider(providerId));
  const provider = selectedProvider ?? providerById;
  const isInternationalProvider =
    provider?.country && provider.country !== 'United States';

  const formValues = watch();
  const {date: formDate, time: formTime, timezone: formTimezone} = formValues;

  const utmSource = useQuery().get('utm_source') ?? '';

  const providerCalendar = Object.keys(slots ?? {}).length
    ? slots?.[providerId]
    : [];
  const {error, datetime} = BookingService.getAppointmentDateTimeString(
    {
      time: formTime,
      timezone: formTimezone,
      date: formDate as Date,
    },
    providerCalendar,
  );

  const {t} = useTranslation();

  const setSessionData = () => {
    if (error) {
      Toast({type: 'error', message: error});
    } else if (datetime) {
      if (user?.email) {
        dispatch(
          bookAppointment({
            appointmentDateTimeString: datetime,
            providerId: providerId,
            patientTimezone: formTimezone,
            calendarId: provider.calendarId,
            appointmentType,
            appointmentTypeID: String(appointmentTypeID),
            patientEmail: user.email,
          }),
        );
      } else {
        dispatch(
          setDirectBookingSelectedSession({
            appointmentType,
            appointmentTypeId: String(appointmentTypeID),
            appointmentDuration: duration,
            paymentType: filter.selectedOptions?.paymentType,
            timezone: formTimezone,
            datetime,
            providerRole: providerRole!,
            providerState: state,
          }),
        );
      }
      if (isInternationalProvider) {
        history.replace(
          '/practice/register/' + provider.userId + `?utm_source=${utmSource}`,
        );
      } else {
        history.push(
          (filter.selectedOptions?.paymentType === 'cash'
            ? '/plans'
            : '/zd/register') + `?utm_source=${utmSource}`,
        );
      }
    } else {
      Toast({type: 'error', message: 'Please select a time'});
    }
  };

  return (
    <Button
      type="button"
      onClick={setSessionData}
      borderColor="transparent"
      disabled={isLoading || !selectedTime}
      fontWeight="medium"
      className="block rounded-[11px] mx-auto px-8 py-3"
    >
      {user?.email ? t('book', 'Book') : t('continue', 'Continue')}
    </Button>
  );
}
